import { GameStateEngine } from '@/core/game-state-engine'
import { HudLocator } from '@/core/hud-locator'
import { ReadingValidator } from '@/core/reading-validator'
import { metricRecognitionEnabled, recognitionSchedulerIntervalMs } from '@/core/recognition-settings'
import { metricKeys, type CaptureProfile, type GameEvent, type GameStateSnapshot, type MetricKey, type MetricReading, type RawReading, type ValidatedObservation } from '@/domain/types'
import type { GamePreset, HudLocation, PresetRecognitionContext, PresetRecognizer } from '@/presets/types'
import { FrameScheduler, type FrameSchedulerHealth } from './frame-scheduler'

export interface PipelineResult {
  readonly captureGeneration: number
  readonly raw: RawReading
  readonly observation: ValidatedObservation
  readonly state: GameStateSnapshot
  readonly events: readonly GameEvent[]
  readonly hud: HudLocation
}

export function maskDisabledStatisticsReading(
  raw: RawReading,
  recognition: CaptureProfile['recognition'],
): RawReading {
  const disabled = metricKeys.filter((metric) => !metricRecognitionEnabled(recognition, metric))
  if (!disabled.length) return raw
  const result = { ...raw }
  for (const metric of disabled) {
    const source = raw[metric].source
    Object.assign(result, {
      [metric]: {
        value: null,
        confidence: 0,
        source,
        availability: 'unavailable',
        capturedAt: raw.capturedAt,
      } satisfies MetricReading<never>,
    })
  }
  return result
}

export class AnalysisPipeline {
  private readonly locator = new HudLocator()
  private readonly validator: ReadingValidator
  private readonly stateEngine: GameStateEngine
  private readonly recognizer: PresetRecognizer
  private scheduler: FrameScheduler | null = null
  private sessionId = ''
  private captureGeneration = 0
  private diagnosticMetric: MetricKey | null = null
  private lastPrioritySampleAt = 0
  private recognitionRevision = 0

  constructor(
    private readonly preset: GamePreset,
    private profile: CaptureProfile,
    private readonly onResult: (result: PipelineResult) => void,
    private readonly onStalled?: (health: FrameSchedulerHealth) => void,
  ) {
    this.validator = new ReadingValidator(preset, profile.recognition.minConfidence)
    this.stateEngine = new GameStateEngine(preset)
    this.recognizer = preset.createRecognizer(profile)
    this.recognizer.updateRecognitionSettings?.(profile.recognition, null)
  }

  start(video: HTMLVideoElement, sessionId: string, baseline: GameStateSnapshot | null = null, captureGeneration = 0): void {
    this.stop()
    this.sessionId = sessionId
    this.captureGeneration = captureGeneration
    this.lastPrioritySampleAt = 0
    this.recognizer.resetContinuity?.()
    this.validator.resetBaseline(baseline)
    this.stateEngine.resetBaseline(baseline)
    this.recognizer.requestMetrics?.(['exp', 'level'])
    this.resume(video)
  }

  resume(video: HTMLVideoElement): void {
    if (this.scheduler) return
    this.scheduler = new FrameScheduler(video, recognitionSchedulerIntervalMs(this.profile.recognition), {
      initialMediaTime: video.currentTime,
      onStalled: (health) => this.onStalled?.(health),
    })
    this.scheduler.start((capturedAt, signal) => this.process(video, capturedAt, { signal }))
  }

  async settle(video: HTMLVideoElement, requestedMetrics: readonly MetricKey[], signal: AbortSignal): Promise<boolean> {
    await this.suspendScheduler()
    if (signal.aborted) return false
    return this.process(video, Date.now(), { mode: 'settlement', requestedMetrics, signal })
  }

  async prepareForSessionStart(signal: AbortSignal): Promise<void> {
    await this.suspendScheduler()
    if (signal.aborted) return
    await this.recognizer.preparePriorityRecognition?.(Number.POSITIVE_INFINITY, signal)
  }

  async sampleForSessionStart(video: HTMLVideoElement, requestedMetrics: readonly MetricKey[], signal: AbortSignal): Promise<boolean> {
    await this.prepareForSessionStart(signal)
    if (signal.aborted) return false
    const capturedAt = Math.max(Date.now(), this.lastPrioritySampleAt + 1)
    this.lastPrioritySampleAt = capturedAt
    return this.process(video, capturedAt, { mode: 'settlement', requestedMetrics, signal })
  }

  async beginSession(
    video: HTMLVideoElement,
    sessionId: string,
    baseline: GameStateSnapshot,
    captureGeneration: number,
    minimumSourceAt: number,
  ): Promise<void> {
    await this.suspendScheduler()
    this.sessionId = sessionId
    this.captureGeneration = captureGeneration
    this.lastPrioritySampleAt = minimumSourceAt
    this.validator.resetBaseline(baseline)
    this.stateEngine.resetBaseline(baseline)
    if (this.recognizer.beginSession) this.recognizer.beginSession(minimumSourceAt)
    else this.recognizer.requestMetrics?.(['exp'])
    this.resume(video)
  }

  updateProfile(profile: CaptureProfile): void {
    const changedContinuity = metricKeys.filter((metric) => (
      metricRecognitionEnabled(this.profile.recognition, metric)
      !== metricRecognitionEnabled(profile.recognition, metric)
    ))
    this.profile = profile
    this.recognitionRevision += 1
    this.validator.setMinimumConfidence(profile.recognition.minConfidence)
    if (changedContinuity.length) {
      this.validator.resetMetricContinuity(changedContinuity)
      this.stateEngine.markMetricsDiscontinuous(changedContinuity)
    }
    this.recognizer.updateRecognitionSettings?.(profile.recognition, this.diagnosticMetric)
    this.scheduler?.setInterval(recognitionSchedulerIntervalMs(profile.recognition))
    this.scheduler?.pulse(true)
  }

  setDiagnosticMetric(metric: MetricKey | null): void {
    if (metric === this.diagnosticMetric) return
    this.diagnosticMetric = metric
    this.recognitionRevision += 1
    this.recognizer.updateRecognitionSettings?.(this.profile.recognition, metric)
    this.scheduler?.setInterval(recognitionSchedulerIntervalMs(this.profile.recognition))
    this.scheduler?.pulse(true)
  }

  pulse(force = false): boolean {
    return this.scheduler?.pulse(force) ?? false
  }

  health(): FrameSchedulerHealth | null {
    return this.scheduler?.health() ?? null
  }

  setCaptureGeneration(captureGeneration: number, adaptiveMetricToReset?: MetricKey): void {
    if (captureGeneration === this.captureGeneration) return
    this.captureGeneration = captureGeneration
    this.recognizer.resetContinuity?.(adaptiveMetricToReset)
  }

  resetBaseline(adaptiveMetricToReset?: MetricKey): void {
    this.recognizer.resetContinuity?.(adaptiveMetricToReset)
    this.validator.resetBaseline()
    this.stateEngine.resetBaseline()
    this.recognizer.requestMetrics?.(['exp', 'level'])
  }

  stop(): void {
    this.scheduler?.stop()
    this.scheduler = null
  }

  dispose(): void {
    this.stop()
    this.recognizer.dispose()
  }

  private async suspendScheduler(): Promise<void> {
    const scheduler = this.scheduler
    scheduler?.stop()
    this.scheduler = null
    try {
      await scheduler?.waitForIdle()
    } catch {
      // A failed scheduled frame must not prevent a priority sample.
    }
  }

  private async process(
    video: HTMLVideoElement,
    capturedAt: number,
    options: Pick<PresetRecognitionContext, 'mode' | 'requestedMetrics' | 'signal'> = {},
  ): Promise<boolean> {
    if (!video.videoWidth || !video.videoHeight || options.signal?.aborted) return false
    const captureGeneration = this.captureGeneration
    const recognitionRevision = this.recognitionRevision
    const bitmap = await createImageBitmap(video)
    try {
      if (options.signal?.aborted) return false
      const hud = this.locator.locate(bitmap, this.preset, bitmap.width, bitmap.height)
      const rawResult = await this.recognizer.recognize({
        frame: bitmap,
        profile: this.profile,
        capturedAt,
        hudRect: hud.rect,
        diagnosticMetric: this.diagnosticMetric,
        ...options,
      })
      if (captureGeneration !== this.captureGeneration
        || recognitionRevision !== this.recognitionRevision
        || options.signal?.aborted) return false
      const raw: RawReading = { ...rawResult, diagnostics: { ...rawResult.diagnostics, hudConfidence: hud.confidence } }
      const observation = this.validator.validate(maskDisabledStatisticsReading(raw, this.profile.recognition))
      const { state, events } = this.stateEngine.apply(this.sessionId, observation)
      this.onResult({ captureGeneration, raw, observation, state, events, hud })
      return true
    } finally {
      bitmap.close()
    }
  }

}
